/**
 * Upload multipart S3 depuis le navigateur — MULTIPART-002.
 *
 * POURQUOI CE MODULE EXISTE. Un PUT unique sur une URL presignee plafonne a
 * 5 GiB, plafond dur AWS. Un coach (GFCA, 06/08/2026) a un match de plus de
 * 5 Go et ne peut tout simplement pas l'envoyer : le refus tombe apres le
 * transfert, pas avant. Le multipart decoupe le fichier en parties signees
 * une a une, chacune tres en dessous du plafond.
 *
 * Le decoupage, la concurrence, les reprises et l'abandon sont a la charge
 * du navigateur. Le backend n'expose que la signature (MULTIPART-001).
 *
 * TROIS REGLES QUI NE SE DEVINENT PAS EN LISANT LE CODE :
 *
 * 1. SLASH FINAL SUR LES 4 ROUTES DE CONTROLE. Le routeur backend n'a pas
 *    `redirect_slashes=False` · une URL sans slash final prend un 307, et un
 *    307 DROP l'en-tete Authorization. Le 401 qui suit n'est pas un simple
 *    echec d'upload : l'intercepteur de lib/axios.js efface le token et
 *    redirige vers /login. Le coach serait ejecte de son wizard, formulaire
 *    perdu, au milieu d'un transfert de plusieurs gigaoctets.
 *
 * 2. `fetch` + authHeaders(), PAS l'instance `api`. Meme raison, prise par
 *    l'autre bout · sur un envoi de 6 Go, le token peut expirer EN COURS de
 *    transfert. L'instance `api` reagirait a ce 401 en effacant la session et
 *    en redirigeant — 40 minutes de transfert perdues pour un probleme de
 *    session recuperable. On rend ici une erreur explicite et on laisse le
 *    coach relancer. Le prix assume · l'extraction du `detail` FastAPI
 *    (BUG-011) est refaite a la main plus bas.
 *
 * 3. LES PUT DE PARTIES PARTENT NUS. Ni Authorization (il invaliderait la
 *    signature S3, cf. ARCHI-001), ni Content-Type · le backend ne signe pas
 *    de content-type sur `upload_part`, en ajouter un ferait echouer la
 *    signature.
 */
import { parseS3Error, s3ErrorLabel } from '../utils/s3Error'

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

// Duplique de CreateMatch.jsx faute de helper partage · ce module ne peut pas
// dependre d'une page.
function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

/** Formats acceptes, alignes sur MULTIPART_ALLOWED_EXTENSIONS du backend. */
export const EXTENSIONS_VIDEO = ['mp4', 'mov', 'm4v']

/** 8 GiB — meme borne que le backend, qui refuse au-dela en 400. */
export const TAILLE_MAX_VIDEO = 8 * 1024 * 1024 * 1024

/**
 * Valide un fichier AVANT tout transfert.
 *
 * Le coach doit apprendre qu'un .mkv est refuse a la seconde ou il le
 * choisit, pas apres avoir attendu. Rend null si le fichier passe, sinon le
 * message a afficher.
 *
 * @param {File} file
 * @returns {string|null}
 */
export function verifierFichierVideo(file) {
  if (!file) return 'Aucun fichier sélectionné.'

  if (file.size > TAILLE_MAX_VIDEO) {
    return 'Fichier trop volumineux — limite 8GB.'
  }
  if (file.size === 0) {
    return 'Ce fichier est vide.'
  }

  const nom = file.name || ''
  if (!nom.includes('.')) {
    return 'Fichier sans extension — utilise un .mp4, .mov ou .m4v.'
  }
  const ext = nom.split('.').pop().toLowerCase()
  if (!EXTENSIONS_VIDEO.includes(ext)) {
    return `Format .${ext} non pris en charge — utilise un .mp4, .mov ou .m4v.`
  }

  return null
}

/**
 * Marque une erreur comme METIER · un refus attendu, pas un defaut.
 *
 * POURQUOI CE DRAPEAU. Un quota FREE atteint ou un .mkv refuse n'est pas une
 * panne : c'est le produit qui fonctionne comme prevu. Les envoyer dans Sentry
 * noierait exactement ce qu'UPLOAD-ERR-001 vient de rendre visible — les vrais
 * refus S3, dont on ne voit qu'une poignee par semaine. Le coach voit le meme
 * message dans les deux cas ; seule la remontee technique differe.
 *
 * Le 401 « session expiree » n'est PAS metier · il signale un probleme de
 * session qu'on veut voir passer.
 */
function marquerMetier(err) {
  err.metier = true
  return err
}

// 4 parties en vol au maximum. Au-dela, sur une connexion de coach en 4G, les
// parties se volent la bande passante : le debit total ne monte plus et chaque
// partie individuelle s'approche du delai d'expiration.
const PARTIES_EN_PARALLELE = 4

// 3 tentatives par partie, 1s / 2s / 4s entre elles. Une coupure de quelques
// secondes en 4G ne doit pas condamner un transfert de 40 minutes.
const TENTATIVES_MAX = 3
const ATTENTES_MS = [1000, 2000, 4000]

/**
 * Delais du chien de garde — UPLOAD-TIMEOUT-001.
 *
 * POURQUOI UN CHIEN DE GARDE ET PAS `xhr.timeout` SEUL. `xhr.timeout` borne la
 * DUREE TOTALE de la requete. Or une partie de 100 Mio n'a pas de duree
 * normale · elle depend du lien montant du coach et des trois autres parties
 * qui le partagent. Mesure chez un coach le 06/08/2026 · ses parties 1 a 4 ont
 * pris 19 secondes, les parties 5 a 8 en ont pris 300. Un plafond de 2 minutes
 * par partie, defendable sur le papier, aurait fait echouer TOUTES ses parties
 * et tue l'upload qu'on cherchait a sauver.
 *
 * Ce qu'on veut distinguer, ce n'est pas « lent » de « rapide » · c'est
 * « avance » de « fige ». On surveille donc l'INACTIVITE · si aucun octet ne
 * part pendant 90 secondes, la partie est morte, on la rejoue. Une partie qui
 * rampe a 50 Ko/s n'est jamais punie.
 *
 * LE DEFAUT QUE CA CORRIGE. Sans cela, une requete suspendue n'echoue jamais ·
 * XMLHttpRequest n'a aucun timeout par defaut. La reprise ci-dessus ne se
 * declenche que sur une ERREUR, donc jamais. Le coach reste devant une barre
 * figee, sans message et sans reprise, indefiniment. Trois uploads perdus de
 * cette facon en une matinee.
 *
 * Le plafond absolu ne sert qu'au cas ou la requete ne demarre meme pas, sans
 * jamais emettre un seul `onprogress`. Volontairement tres large.
 *
 * Modifiables pour les tests · attendre 90 secondes reelles rendrait le
 * harnais inutilisable, et ces deux valeurs sont exactement ce qu'il doit
 * exercer.
 */
export const DELAIS_PARTIE = {
  inactiviteMs: 90 * 1000,
  plafondAbsoluMs: 30 * 60 * 1000,
}

const attendre = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Lit le `detail` d'une reponse d'erreur FastAPI.
 *
 * Reprise manuelle de ce que fait l'intercepteur de lib/axios.js · on ne passe
 * pas par lui (voir regle 2 en tete de fichier), donc on refait le minimum
 * utile. Ne leve jamais · un corps illisible rend null, l'appelant a un repli.
 */
async function lireDetailFastAPI(response) {
  try {
    const data = await response.json()
    const detail = data?.detail

    if (typeof detail === 'string' && detail.trim()) return detail
    // Erreur metier structuree (ex. FREE_VIDEO_LIMIT_REACHED).
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message
    }
    // Validation Pydantic.
    if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') return detail[0].msg

    return null
  } catch {
    return null
  }
}

/**
 * Appelle une des 4 routes de controle. Slash final obligatoire dans `chemin`.
 */
async function appelerBackend(chemin, corps, signal) {
  let response
  try {
    response = await fetch(`${API}${chemin}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(corps),
      signal,
    })
  } catch (e) {
    if (e?.name === 'AbortError') throw e
    throw new Error('Connexion impossible, vérifie ta connexion.')
  }

  if (response.status === 401) {
    // Volontairement PAS de redirection ni d'effacement de token · voir la
    // regle 2. Le coach garde son formulaire et peut relancer.
    const err = new Error("Ta session a expiré. Recharge la page et relance l'envoi.")
    err.httpStatus = 401
    throw err
  }

  if (!response.ok) {
    const detail = await lireDetailFastAPI(response)
    const err = new Error(detail || `Envoi refusé par le serveur (HTTP ${response.status}).`)
    err.httpStatus = response.status
    // 400 et 403 sont les refus que le backend emet volontairement · quota FREE
    // atteint, plan sans droit d'upload, extension ou taille hors bornes. Ce
    // sont des reponses, pas des pannes.
    if (response.status === 400 || response.status === 403) marquerMetier(err)
    throw err
  }

  return response.json()
}

/**
 * Envoie UNE partie sur son URL signee et rend son ETag.
 *
 * XHR et non fetch · `xhr.upload.onprogress` est la seule facon de suivre les
 * octets reellement partis. `fetch` ne rapporte aucune progression a l'upload.
 */
function envoyerPartie({ url, blob, onOctets, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let dejaComptes = 0

    // Chien de garde d'inactivite · voir DELAIS_PARTIE. `figee` distingue notre
    // propre abandon d'une annulation par l'utilisateur · les deux passent par
    // xhr.abort(), mais l'un est rejouable et l'autre non.
    let figee = false
    let minuteur = null
    const rearmer = () => {
      clearTimeout(minuteur)
      minuteur = setTimeout(() => {
        figee = true
        xhr.abort()
      }, DELAIS_PARTIE.inactiviteMs)
    }

    const surAnnulation = () => xhr.abort()
    signal?.addEventListener('abort', surAnnulation)
    const nettoyer = () => {
      clearTimeout(minuteur)
      signal?.removeEventListener('abort', surAnnulation)
    }

    xhr.upload.onprogress = (e) => {
      // Le compteur repart a chaque octet parti · c'est l'ABSENCE de
      // progression qu'on chasse, pas la lenteur.
      rearmer()
      if (!e.lengthComputable) return
      onOctets(e.loaded - dejaComptes)
      dejaComptes = e.loaded
    }

    // `responseType` reste non positionne · le fixer viderait `responseText`,
    // seule source du corps <Error> de S3 (UPLOAD-ERR-001).
    xhr.onload = () => {
      nettoyer()

      if (xhr.status >= 400) {
        const { code, message, requestId } = parseS3Error(xhr.responseText)
        const err = new Error(s3ErrorLabel(code, xhr.status))
        err.s3Code = code
        err.s3Message = message
        err.s3RequestId = requestId
        err.httpStatus = xhr.status
        return reject(err)
      }

      const etag = xhr.getResponseHeader('ETag')
      if (!etag) {
        // S3 expose ETag par defaut et le bucket le declare bien en
        // ExposeHeaders. Un ETag absent signifie que la reponse a ete lue a
        // travers une politique CORS qui ne l'expose pas · aucune reprise ne
        // corrigera ca, et sans ETag la finalisation est impossible.
        const err = new Error(
          "Le stockage n'a pas renvoyé l'identifiant de la partie. Contacte le support.",
        )
        err.etagManquant = true
        return reject(err)
      }

      resolve(etag)
    }

    xhr.onerror = () => {
      nettoyer()
      reject(new Error("Erreur réseau pendant l'envoi."))
    }
    xhr.ontimeout = () => {
      nettoyer()
      // Ni httpStatus ni name='AbortError' · estRejouable la classe donc en
      // erreur reseau, et la reprise la rejoue. C'est tout l'objet du lot.
      reject(new Error("L'envoi de cette partie n'a pas abouti."))
    }
    xhr.onabort = () => {
      nettoyer()
      if (figee) {
        // Notre propre abandon, pas celui du coach · rejouable.
        return reject(new Error("L'envoi de cette partie s'est interrompu."))
      }
      const err = new Error("Envoi annulé.")
      err.name = 'AbortError'
      reject(err)
    }

    xhr.open('PUT', url)
    // Plafond absolu · ne couvre que la requete qui n'emet jamais le moindre
    // `onprogress`, le chien de garde s'occupant de tout le reste.
    xhr.timeout = DELAIS_PARTIE.plafondAbsoluMs
    // Aucun en-tete ajoute : ni Authorization, ni Content-Type (regle 3).
    xhr.send(blob)
    // Arme avant le premier octet · une requete qui ne demarre pas du tout doit
    // aussi finir par tomber.
    rearmer()
  })
}

/**
 * Une erreur merite-t-elle une nouvelle tentative ?
 *
 * Reessayer un refus definitif ne fait que retarder l'echec de 7 secondes et
 * consommer trois fois la bande passante du coach. On ne rejoue que ce qui est
 * plausiblement transitoire · reseau coupe, 5xx, saturation.
 */
function estRejouable(err) {
  if (err?.name === 'AbortError') return false
  if (err?.etagManquant) return false

  const status = err?.httpStatus
  if (!status) return true // erreur reseau : rejouable
  return status >= 500 || status === 408 || status === 429
}

/**
 * Envoie un fichier en multipart et rend son file_key S3.
 *
 * @param {File} file
 * @param {{onProgress?: (pourcentage: number) => void, signal?: AbortSignal}} options
 * @returns {Promise<string>} file_key
 */
export async function uploadFileMultipart(file, { onProgress, signal } = {}) {
  const invalide = verifierFichierVideo(file)
  if (invalide) throw marquerMetier(new Error(invalide))

  // ── 1. Ouverture de l'upload ────────────────────────────────────────
  const { upload_id, file_key, part_size, part_count } = await appelerBackend(
    '/upload/multipart/create/',
    {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      file_size: file.size,
    },
    signal,
  )

  if (!upload_id || !file_key || !part_size || !part_count) {
    throw new Error("Réponse inattendue du serveur à l'ouverture de l'envoi.")
  }

  // Controleur interne · sert a stopper les parties encore en vol des qu'une
  // partie echoue definitivement, sans attendre qu'elles finissent. Relie au
  // signal de l'appelant s'il y en a un.
  const controleur = new AbortController()
  const relayer = () => controleur.abort()
  signal?.addEventListener('abort', relayer)

  // ── 2. Progression sur les octets reels ─────────────────────────────
  //
  // Une barre qui saute de 0 a 25% a chaque partie terminee est pire que pas
  // de barre : sur 4 parties elle reste figee des minutes entieres. On somme
  // les octets effectivement partis, par partie · une partie rejouee repart de
  // zero et n'ajoute donc pas deux fois ses octets a l'ensemble.
  const octetsParPartie = new Map()
  const rapporterProgression = () => {
    if (!onProgress) return
    let total = 0
    for (const octets of octetsParPartie.values()) total += octets
    // Plafonne a 99 · la finalisation cote S3 n'est pas instantanee sur un
    // fichier de plusieurs Go. Afficher 100 avant qu'elle rende la main
    // laisserait le coach devant une barre pleine et un ecran fige.
    onProgress(Math.min(99, Math.floor((total * 100) / file.size)))
  }

  // ── 3. File d'attente · 4 parties en parallele au maximum ───────────
  const etags = new Map() // PartNumber -> ETag
  let prochainePartie = 1
  let echec = null

  async function traiterUnePartie(numero) {
    const debut = (numero - 1) * part_size
    const fin = Math.min(debut + part_size, file.size)
    const blob = file.slice(debut, fin)

    let derniereErreur = null

    for (let tentative = 0; tentative < TENTATIVES_MAX; tentative++) {
      if (controleur.signal.aborted) {
        const err = new Error('Envoi annulé.')
        err.name = 'AbortError'
        throw err
      }

      if (tentative > 0) {
        // Les octets de la tentative ratee ne comptent plus · sans cette
        // remise a zero, la barre depasserait 100% sur une reprise.
        octetsParPartie.set(numero, 0)
        rapporterProgression()
        await attendre(ATTENTES_MS[tentative - 1])
      }

      try {
        // L'URL est signee juste avant l'envoi de la partie · sur un transfert
        // long, une URL signee au depart pour la derniere partie aurait vieilli
        // de toute la duree de l'upload.
        const { url } = await appelerBackend(
          '/upload/multipart/sign-part/',
          { file_key, upload_id, part_number: numero },
          controleur.signal,
        )

        const etag = await envoyerPartie({
          url,
          blob,
          signal: controleur.signal,
          onOctets: (delta) => {
            octetsParPartie.set(numero, (octetsParPartie.get(numero) || 0) + delta)
            rapporterProgression()
          },
        })

        etags.set(numero, etag)
        // La partie est acceptee : on fige sa contribution a sa taille reelle,
        // `onprogress` ne rapportant pas toujours le dernier bloc.
        octetsParPartie.set(numero, blob.size)
        rapporterProgression()
        return
      } catch (err) {
        derniereErreur = err
        if (!estRejouable(err)) throw err
      }
    }

    throw derniereErreur
  }

  async function ouvrier() {
    while (!echec) {
      const numero = prochainePartie++
      if (numero > part_count) return
      try {
        await traiterUnePartie(numero)
      } catch (err) {
        // Le premier echec definitif arrete tout le monde · inutile de
        // continuer a consommer la connexion du coach pour un upload qui ne
        // sera pas finalisable.
        if (!echec) {
          echec = err
          controleur.abort()
        }
        return
      }
    }
  }

  const ouvriers = Array.from(
    { length: Math.min(PARTIES_EN_PARALLELE, part_count) },
    () => ouvrier(),
  )

  try {
    await Promise.all(ouvriers)

    if (echec) throw echec
    if (signal?.aborted) {
      const err = new Error('Envoi annulé.')
      err.name = 'AbortError'
      throw err
    }

    // ── 4. Finalisation ───────────────────────────────────────────────
    // Tri croissant strict impose par S3.
    const parts = [...etags.entries()]
      .sort(([a], [b]) => a - b)
      .map(([PartNumber, ETag]) => ({ PartNumber, ETag }))

    if (parts.length !== part_count) {
      throw new Error("L'envoi est incomplet, relance-le.")
    }

    await appelerBackend('/upload/multipart/complete/', { file_key, upload_id, parts })

    if (onProgress) onProgress(100)
    return file_key
  } catch (err) {
    // ── 5. Abandon ────────────────────────────────────────────────────
    // Sans cet appel, les parties deja stockees restent facturees jusqu'au
    // passage de la lifecycle rule. Best-effort · son propre echec ne doit pas
    // masquer la cause reelle, qui est ce qu'on relance.
    try {
      await appelerBackend('/upload/multipart/abort/', { file_key, upload_id })
    } catch {
      /* rien : l'erreur d'origine prime */
    }
    throw err
  } finally {
    signal?.removeEventListener('abort', relayer)
  }
}

export default uploadFileMultipart
